import { Divider, Paper, Stack, Typography } from '@mui/material';
import { BudgetItemsQuery, CostItemEntryType, ExpenseStatus } from 'gql/index';
import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';

type BudgetItem = BudgetItemsQuery['budgetItems'][number];

interface Props {
  budgetItems: BudgetItem[];
}

export const BudgetItemTotals: React.FC<Props> = ({ budgetItems }) => {
  const { formatMessage, formatNumber } = useIntl();

  const totals = useMemo(() => {
    return budgetItems.reduce((acc, budgetItem) => {
      (budgetItem.costItems ?? []).forEach(costItem => {
      

        const { spentAmount, quotedAmount } = (costItem.entries ?? []).reduce((entryAcc, entry) => {
          if (entry.type === CostItemEntryType.Expense && entry.expense) {
            const amount = Number(entry.expense.expendedAmount ?? 0);
            const paid = entry.expense.status === ExpenseStatus.Paid ? amount : 0;
            const implicitQuoteAmount = entry.expense.quoteId == null ? amount : 0; // expenses without quotes are considered as implicit quotes
            return {
              ...entryAcc,
              spentAmount: entryAcc.spentAmount + paid,
              quotedAmount: entryAcc.quotedAmount + implicitQuoteAmount,
            };
          }

          if (entry.type === CostItemEntryType.Quote && entry.quote) {
            const amount = Number(entry.quote.amount ?? 0);
            return { ...entryAcc, quotedAmount: entryAcc.quotedAmount + amount };
          }

          return entryAcc;
        }, { spentAmount: 0, quotedAmount: 0 });

        const toPayAmount = Math.max(0, quotedAmount - spentAmount);
        acc.spent += spentAmount;
        acc.toPay += toPayAmount;
        acc.quoted += quotedAmount;
      });

      
      const effectiveAmount = budgetItem.costItems?.reduce((acc, costItem) => acc + (costItem.effectiveAmount ?? 0), 0) ?? 0;
      const overrunAmount = budgetItem.budgetedAmount > 0 ? Math.max(effectiveAmount - budgetItem.budgetedAmount, 0) : 0;
      acc.planned += budgetItem.budgetedAmount ?? 0;
      acc.overrun += overrunAmount;
      return acc;
    }, {
      planned: 0,
      spent: 0,
      toPay: 0,
      quoted: 0,
      overrun: 0,
    });
  }, [budgetItems]);

  const formatMoney = useCallback((value: number) => {
    return formatNumber(value, {
      style: 'currency',
      currency: 'CAD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }, [formatNumber]);

  return (
    <Paper variant='outlined' sx={{ p: 2 }}>
      <Stack gap={1}>
        <Typography variant='subtitle1' fontWeight={600}>
          {formatMessage({ id: 'Totals' })}
        </Typography>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          gap={2}
          divider={<Divider orientation='vertical' flexItem />}
        >
          <Stack>
            <Typography variant='body2' color='text.secondary'>
              {formatMessage({ id: 'Total planned' })}
            </Typography>
            <Typography variant='h6'>{formatMoney(totals.planned)}</Typography>
          </Stack>
          <Stack>
            <Typography variant='body2' color='text.secondary'>
              {formatMessage({ id: 'Total quoted' })}
            </Typography>
            <Typography variant='h6'>{formatMoney(totals.quoted)}</Typography>
          </Stack>
          <Stack>
            <Typography variant='body2' color='text.secondary'>
              {formatMessage({ id: 'Total to pay' })}
            </Typography>
            <Typography variant='h6'>{formatMoney(totals.toPay)}</Typography>
          </Stack>
          <Stack>
            <Typography variant='body2' color='text.secondary'>
              {formatMessage({ id: 'Total spent' })}
            </Typography>
            <Typography variant='h6'>{formatMoney(totals.spent)}</Typography>
          </Stack>
          <Stack>
            <Typography variant='body2' color='text.secondary'>
              {formatMessage({ id: 'Total overrun' })}
            </Typography>
            <Typography variant='h6' color={totals.overrun > 0 ? 'error.main' : 'success.main'}>
              {formatMoney(totals.overrun)}
            </Typography>
          </Stack>
        </Stack>
      </Stack>
    </Paper>
  );
};
